import React, { useEffect, useState, useMemo } from 'react';
import { MeshGradient } from '@paper-design/shaders-react';
import { useMousePosition } from '../../hooks/useMousePosition';
import { useReducedMotion } from '../../hooks/useReducedMotion';

interface ShaderBackgroundProps {
  className?: string;
  interactive?: boolean;
}

const ShaderBackground: React.FC<ShaderBackgroundProps> = ({
  className = '',
  interactive = true
}) => {
  const prefersReducedMotion = useReducedMotion();
  const mousePosition = useMousePosition();
  const [mounted, setMounted] = useState(false);
  const [key, setKey] = useState(0);

  // Ensure shader is mounted after initial render to avoid hydration issues
  useEffect(() => {
    setMounted(true);

    // Force re-render shader on visibility change (when tab becomes active again)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setKey(prev => prev + 1);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Teal-themed colors for coaching brand - all in the teal/cyan family
  const colors = useMemo(() => [
    '#0f766e', // primary teal (teal-700)
    '#5eead4', // teal-300 - light teal
    '#14b8a6', // teal-500 - secondary teal
    '#99f6e4', // teal-200 - very light teal/mint
  ], []);

  // Calculate gradient offset based on mouse position
  // Mouse position is normalized 0-1, we want subtle offset
  const offsetX = interactive && !prefersReducedMotion
    ? (mousePosition.x - 0.5) * 0.1
    : 0;
  const offsetY = interactive && !prefersReducedMotion
    ? (mousePosition.y - 0.5) * 0.1
    : 0;

  // Don't render shader until mounted (prevents SSR/hydration issues)
  if (!mounted) {
    return (
      <div className={`absolute inset-0 bg-gradient-to-br from-teal-100 via-teal-50 to-white ${className}`} />
    );
  }

  return (
    <div className={`absolute inset-0 overflow-hidden ${className}`}>
      <MeshGradient
        key={key}
        colors={colors}
        speed={prefersReducedMotion ? 0 : 0.15}
        distortion={0.8}
        style={{
          width: '110%',
          height: '110%',
          marginLeft: '-5%',
          marginTop: '-5%',
          transform: `translate(${offsetX * 50}px, ${offsetY * 50}px)`,
          transition: 'transform 0.3s ease-out'
        }}
      />

      {/* Noise overlay for paper/grainy texture */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none opacity-[0.15] mix-blend-overlay"
        aria-hidden="true"
      >
        <defs>
          <filter id="noise-filter">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.8"
              numOctaves="4"
              stitchTiles="stitch"
            />
          </filter>
        </defs>
        <rect
          width="100%"
          height="100%"
          filter="url(#noise-filter)"
        />
      </svg>

      {/* Subtle vignette for depth */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.05) 100%)'
        }}
        aria-hidden="true"
      />
    </div>
  );
};

export default ShaderBackground;
